import { Typography } from '@material-ui/core'
import Collapse from '@material-ui/core/Collapse'
import CssBaseline from '@material-ui/core/CssBaseline'
import Drawer from '@material-ui/core/Drawer'
import List from '@material-ui/core/List'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import MenuItem from '@material-ui/core/MenuItem'
import { createStyles, makeStyles, useTheme } from '@material-ui/core/styles'
import CentreOperationsIcon from '@material-ui/icons/Apartment'
import InventoryIcon from '@material-ui/icons/Archive'
import VisitorIcon from '@material-ui/icons/AssignmentInd'
import CentreAuditIcon from '@material-ui/icons/AssignmentTurnedIn'
import PortfolioIcon from '@material-ui/icons/BusinessCenterOutlined'
import ChatIcon from '@material-ui/icons/Chat'
import ClassIcon from '@material-ui/icons/Class'
import MocksIcon from '@material-ui/icons/Create'
import ParentDashboardIcon from '@material-ui/icons/Dashboard'
import DescriptionRounded from '@material-ui/icons/DescriptionRounded'
import FieldTripIcon from '@material-ui/icons/DirectionsBusOutlined'
import DvrIcon from '@material-ui/icons/Dvr'
import EventIcon from '@material-ui/icons/Event'
import LogoutIcon from '@material-ui/icons/ExitToApp'
import ExpandLess from '@material-ui/icons/ExpandLess'
import ExpandMore from '@material-ui/icons/ExpandMore'
import ApartmentsIcon from '@material-ui/icons/HomeWork'
import AtHomeIcon from '@material-ui/icons/Home'
import EnrolmentIcon from '@material-ui/icons/HowToReg'
import LanguageIcon from '@material-ui/icons/Language'
import AttendanceIcon from '@material-ui/icons/LibraryAddCheck'
import HelpIcon from '@material-ui/icons/LiveHelp'
import CashRegisterIcon from '@material-ui/icons/LocalAtm'
import PaymentIcon from '@material-ui/icons/Payment'
import PeopleIcon from '@material-ui/icons/People'
import RoomIcon from '@material-ui/icons/Room'
import ScheduleIcon from '@material-ui/icons/Schedule'
import ProgressTrackingIcon from '@material-ui/icons/TrackChanges'
import RevenueGoalsIcon from '@material-ui/icons/TrendingUp'
import clsx from 'clsx'
import { isEmpty } from 'lodash'
import PropTypes from 'prop-types'
import React, { useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation, useHistory } from 'react-router-dom'

import { fetchUnreadCount } from 'api/chat'
import BruceWidget from 'app/components/bruce/BruceWidget'
import {
  MobileBetterOnDesktop,
  MobileBottomNav,
  MobileTopBar
} from 'app/components/generic/MobileShell'
import { drawerWidth, navigationMenus, navigationPaths } from 'app/config'
import { SUPPORT_FORM_LINK } from 'app/constants'
import { getLoggedInUserName, isUserAuthenticated } from 'app/helpers'
import { useAccess } from 'app/hooks/useAccess'
import { useIsMobile } from 'app/hooks/useIsMobile'
import {
  pruneMenusByAccess,
  pruneMenusByBruceOwner,
  pruneMenusBySuperAdmin
} from 'app/permissions'
import { updateDrawerNavigationMenuState } from 'app/store/actions/app'
import { dispatchLogoutStart } from 'app/store/dispatcher/auth'
import { loadMyAccess } from 'app/store/thunks/access'
import { mobile as mobileTokens } from 'app/theme/tokens'
import { useWebSocket } from 'app/WebSocketProvider'

// Walk navigationMenus (which may have nested subMenus) to find the entry
// whose path matches the current pathname. Used to derive the page title
// shown on the mobile top bar. Falls back to "alfred" when nothing matches.
const findMenuItemByPath = (menus, pathname) => {
  for (const item of menus || []) {
    if (item.path === pathname) return item
    if (item.subMenus) {
      const found = findMenuItemByPath(item.subMenus, pathname)
      if (found) return found
    }
  }
  return null
}

const menuIcons = {
  Chat: <ChatIcon color="secondary" />,
  FieldTrip: <FieldTripIcon color="secondary" />,
  Class: <ClassIcon color="secondary" />,
  Inventory: <InventoryIcon color="secondary" />,
  Enrolment: <EnrolmentIcon color="secondary" />,
  Language: <LanguageIcon color="secondary" />,
  Payment: <PaymentIcon color="secondary" />,
  People: <PeopleIcon color="secondary" />,
  Room: <RoomIcon color="secondary" />,
  Event: <EventIcon color="secondary" />,
  Logout: <LogoutIcon color="secondary" />,
  Scheduler: <ScheduleIcon color="secondary" />,
  Reports: <DescriptionRounded color="secondary" />,
  BookDemo: <DvrIcon color="secondary" />,
  Mock: <MocksIcon color="secondary" />,
  Portfolio: <PortfolioIcon color="secondary" />,
  Parentdashboard: <ParentDashboardIcon color="secondary" />,
  Attendance: <AttendanceIcon color="secondary" />,
  ProgressTracking: <ProgressTrackingIcon color="secondary" />,
  Help: <HelpIcon color="secondary" />,
  VisitorEntry: <VisitorIcon color="secondary" />,
  CashRegister: <CashRegisterIcon color="secondary" />,
  CentreOperations: <CentreOperationsIcon color="secondary" />,
  CentreAudit: <CentreAuditIcon color="secondary" />,
  RevenueGoals: <RevenueGoalsIcon color="secondary" />,
  Apartments: <ApartmentsIcon color="secondary" />,
  AtHome: <AtHomeIcon color="secondary" />
}

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      display: 'flex'
    },
    drawerContainer: {
      width: '100%'
    },
    list: {
      width: '100%',
      color: theme.palette.secondary.main
    },
    drawer: {
      [theme.breakpoints.up('sm')]: {
        width: drawerWidth,
        flexShrink: 0
      }
    },
    icon: {
      minWidth: '40px'
    },
    menuButton: {
      marginRight: theme.spacing(2),
      [theme.breakpoints.up('sm')]: {
        display: 'none'
      }
    },
    drawerPaper: {
      width: drawerWidth,
      backgroundColor: theme.palette.background.dark,
      borderRight: 'none'
    },
    // Bottom-sheet styling for the mobile nav drawer (anchored "bottom"
    // instead of "left"). Width comes from the viewport, height is capped
    // so the sheet doesn't cover the whole screen, and top corners get
    // rounded so it reads as a sheet rather than the desktop sidebar.
    drawerPaperMobile: {
      width: '100%',
      maxHeight: '85vh',
      backgroundColor: theme.palette.background.dark,
      borderTop: 'none',
      borderTopLeftRadius: 16,
      borderTopRightRadius: 16,
      overflowY: 'auto',
      // Defensive: long nested menu labels + paddingLeft can push children
      // wider than the sheet, which would otherwise add a horizontal scroll
      // inside the bottom drawer.
      overflowX: 'hidden',
      '& .MuiListItem-root, & .MuiTypography-root': {
        whiteSpace: 'normal',
        wordBreak: 'break-word'
      }
    },
    // A small grab handle at the top of the sheet, both as a visual cue
    // ("this is draggable / dismissable") and as a target the user can swipe.
    drawerHandle: {
      width: 36,
      height: 4,
      borderRadius: 2,
      backgroundColor: 'rgba(255,255,255,0.4)',
      margin: '8px auto 4px'
    },
    content: {
      flexGrow: 1,
      // Allow this flex child to shrink below its content's intrinsic width so
      // wide tables scroll inside their own container instead of stretching the
      // page off-screen. Without this a flex item's min-width defaults to its
      // content size (here: the widest table).
      minWidth: 0
    },
    contentMobile: {
      flexGrow: 1,
      minWidth: 0,
      // Reserve space for fixed top bar + bottom nav (incl. iOS safe areas).
      paddingTop: `calc(${mobileTokens.topBarHeight} + ${mobileTokens.safeAreaTop})`,
      paddingBottom: `calc(${mobileTokens.bottomNavHeight} + ${mobileTokens.safeAreaBottom})`,
      minHeight: '100vh',
      width: '100%'
    },
    nested: {
      paddingLeft: theme.spacing(5)
    },
    doubleNested: {
      paddingLeft: theme.spacing(10)
    },
    // A group inside a group (HQ › Centre Operations › …). Every leaf gets
    // the same `nested` indent whatever its depth, so without this a third-
    // level child sat flush with its second-level siblings and you couldn't
    // tell where the group ended. Indent past the parent, draw a thin rule
    // down the left, and set the labels a size smaller.
    subGroup: {
      marginLeft: theme.spacing(6),
      borderLeft: '1px solid rgba(255,255,255,0.18)',
      '& .MuiListItem-root': {
        paddingLeft: theme.spacing(2)
      },
      '& .MuiListItemText-primary': {
        fontSize: '0.85rem',
        opacity: 0.85
      }
    },
    logo: {
      display: 'flex',
      flexDirection: 'row',
      fontSize: 22,
      color: '#FFFFFF',
      marginBottom: 0,
      marginTop: 5
    },
    version: {
      fontSize: 22,
      fontWeight: 'bold',
      marginLeft: 5
    },
    firstName: {
      fontSize: 28,
      fontWeight: 'bold',
      color: theme.palette.primary.main,
      marginTop: '-4px'
    },
    logoContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      padding: theme.spacing(2)
    },
    selected: {
      color: theme.palette.primary.main
    },
    appbarContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexGrow: 1
    },
    themeProfileContainer: {
      display: 'flex',
      flexDirection: 'row'
    },
    headerName: {
      fontWeight: 'bold'
    },
    unreadCount: {
      backgroundColor: '#EB5757',
      borderRadius: '50%',
      width: '1rem',
      height: '1rem',
      marginRight: '1.5rem'
    }
  })
)

const MainLayout = (props) => {
  const classes = useStyles()
  const theme = useTheme()
  const [mobileOpen, setMobileOpen] = React.useState(false)
  const [path, setPath] = React.useState('')
  const isMobile = useIsMobile()

  const { addMessageListener, removeMessageListener } = useWebSocket()

  const drawerNavigationMenuState = useSelector(
    (state) => state.appReducer.drawerNavigationMenuState
  )

  const dispatch = useDispatch()
  const location = useLocation()
  const history = useHistory()

  // Load the user's role + permissions once. MainLayout mounts after login and
  // on every reload, and nothing gated renders outside it.
  //
  // The isUserAuthenticated() guard is load-bearing: this shell also mounts for
  // a LOGGED-OUT user (it wraps the routes, and PrivateRoute only redirects from
  // inside it), and calling /base/me without a token 401s -> global interceptor
  // logs out + reloads -> infinite refresh loop. The thunk guards too; this just
  // avoids the pointless dispatch.
  const { loaded: accessLoaded, access } = useAccess()
  React.useEffect(() => {
    if (!accessLoaded && isUserAuthenticated()) dispatch(loadMyAccess())
  }, [accessLoaded, dispatch])

  // Bruce's owner (the only one who sees HQ > Bruce). Read from the config the
  // Bruce widget already fetches, so the sidebar costs no extra request.
  const [isBruceOwner, setIsBruceOwner] = React.useState(false)
  const onBruceConfig = React.useCallback((data) => {
    setIsBruceOwner(Boolean(data && data.can_manage_access))
  }, [])

  // Sidebar shows only what this role may see. Pruned before renderMenuElement
  // so the auto-collapse logic below operates on the pruned shape.
  // Second pass for items marked superAdminOnly. Separate from the access prune
  // because that one returns early for unrestricted roles, which are exactly
  // who these items hide from. Third, bruceOwnerOnly — same reason.
  const visibleMenus = React.useMemo(
    () =>
      pruneMenusByBruceOwner(
        pruneMenusBySuperAdmin(
          pruneMenusByAccess(navigationMenus, access),
          access
        ),
        isBruceOwner
      ),
    [access, isBruceOwner]
  )

  const loggedInUser = getLoggedInUserName()
  const firstName = loggedInUser
    ? loggedInUser?.split(' ')[0]?.toLowerCase()
    : 'user'

  const [unreadCountNumber, setUnreadCountNumber] = React.useState(0)
  const unreadCountRef = useRef(null)
  unreadCountRef.current = unreadCountNumber

  React.useEffect(() => {
    setPath(location.pathname)
  }, [location])

  React.useEffect(() => {
    const fetchUnreadCountData = async () => {
      const data = await fetchUnreadCount()
      setUnreadCountNumber(data.unread_message_count)
    }

    const timeout = setTimeout(() => {
      fetchUnreadCountData()
    }, 1500)

    return () => clearTimeout(timeout)
  }, [])

  // adding a listener to for unread count updates via WS
  React.useEffect(() => {
    const unreadCountListener = (message) => {
      const parsedMessage = JSON.parse(message)

      if (unreadCountRef.current !== parseInt(parsedMessage.total_unread_count))
        setUnreadCountNumber(parsedMessage.total_unread_count)
    }
    addMessageListener(unreadCountListener)

    return () => {
      removeMessageListener(unreadCountListener)
      console.log('unread count listener removed from mainlayout.js')
    }
  }, [])

  const activeRoute = React.useCallback(
    (route) => {
      return route === path
    },
    [path]
  )

  const handleClick = React.useCallback(
    (menu) => {
      const { nested, name: menuKey } = menu

      if (nested) {
        dispatch(
          updateDrawerNavigationMenuState({
            ...drawerNavigationMenuState,
            [menuKey]: !drawerNavigationMenuState[menuKey]
          })
        )
      } else {
        dispatch(
          updateDrawerNavigationMenuState({
            [menuKey]: !drawerNavigationMenuState[menuKey]
          })
        )
      }
    },
    [dispatch, drawerNavigationMenuState]
  )

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const renderMenuElement = React.useCallback(
    (menu, depth = 0) => {
      return menu.map((item, index) => {
        if (!item.subMenus) {
          if (item.sideBarVisibility === false) {
            return null
          }

          // External link (e.g. Get Help) — not a router route, opens a URL.
          if (item.externalLink) {
            return (
              <MenuItem
                button
                key={index}
                onClick={() => {
                  window.location.href = SUPPORT_FORM_LINK
                }}
                className={clsx(
                  classes.list,
                  item.nested ? classes.nested : null
                )}
              >
                {item.icon && !isEmpty(item.icon.name) && (
                  <ListItemIcon className={clsx(classes.icon)}>
                    {menuIcons[item.icon.name] || null}
                  </ListItemIcon>
                )}
                <ListItemText primary={item.displayName.toLowerCase()} />
              </MenuItem>
            )
          }

          const selected = activeRoute(item.path)
          return (
            <MenuItem
              button
              key={index}
              component={Link}
              to={item.path}
              className={clsx(
                classes.list,
                selected ? classes.selected : null,
                item.doubleNested
                  ? classes.doubleNested
                  : item.nested === false
                  ? null
                  : classes.nested
              )}
              selected={selected}
            >
              {item.icon && !isEmpty(item.icon.name) ? (
                <>
                  <ListItemIcon className={clsx(classes.icon)}>
                    {menuIcons[item.icon.name] || null}
                  </ListItemIcon>
                  <ListItemText primary={item.displayName.toLowerCase()} />
                </>
              ) : (
                <>
                  <ListItemText primary={item.displayName.toLowerCase()} />
                  {item.displayName.toLowerCase() === 'centre-parent chat' &&
                    unreadCountNumber > 0 && (
                      <div className={classes.unreadCount}></div>
                    )}
                </>
              )}
            </MenuItem>
          )
        }

        if (item.sideBarVisibility === false) return null

        // Auto-collapse: if this menu group has exactly one visible leaf
        // child (subMenus[*].sideBarVisibility !== false, no further
        // subMenus of its own), don't render an expand/collapse — render
        // the group itself as a direct Link to that one child's path.
        // Saves a needless tap on Programs/People/etc. after the kills
        // and consolidations reduced most groups to a single live entry.
        const visibleChildren = (item.subMenus || []).filter(
          (s) => s && s.sideBarVisibility !== false
        )
        if (
          !item.noAutoCollapse &&
          visibleChildren.length === 1 &&
          visibleChildren[0].path &&
          !visibleChildren[0].subMenus
        ) {
          const child = visibleChildren[0]
          const selected = activeRoute(child.path)
          return (
            <MenuItem
              button
              key={index}
              component={Link}
              to={child.path}
              className={clsx(
                classes.list,
                selected ? classes.selected : null,
                item.nested ? classes.nested : null
              )}
              selected={selected}
            >
              {item.icon && !isEmpty(item.icon.name) ? (
                <>
                  <ListItemIcon className={clsx(classes.icon)}>
                    {menuIcons[item.icon.name] || null}
                  </ListItemIcon>
                  <ListItemText primary={item.displayName.toLowerCase()} />
                </>
              ) : (
                <ListItemText primary={item.displayName.toLowerCase()} />
              )}
            </MenuItem>
          )
        }

        return (
          <React.Fragment key={index}>
            <MenuItem
              button
              onClick={() => handleClick(item)}
              className={clsx(
                classes.list,
                item.nested ? classes.nested : null
              )}
            >
              {item.icon && !isEmpty(item.icon.name) ? (
                <>
                  <ListItemIcon className={clsx(classes.icon)}>
                    {menuIcons[item.icon.name] || null}
                  </ListItemIcon>
                  <ListItemText primary={item.displayName.toLowerCase()} />
                  {item.displayName.toLowerCase() === 'communication' &&
                    unreadCountNumber > 0 && (
                      <div className={classes.unreadCount}></div>
                    )}
                </>
              ) : (
                <ListItemText primary={item.displayName.toLowerCase()} />
              )}
              {drawerNavigationMenuState[item.name] ? (
                <ExpandLess />
              ) : (
                <ExpandMore />
              )}
            </MenuItem>
            <Collapse
              in={drawerNavigationMenuState[item.name]}
              timeout="auto"
              unmountOnExit
            >
              {depth > 0 ? (
                <div className={classes.subGroup}>
                  {renderMenuElement(item.subMenus, depth + 1)}
                </div>
              ) : (
                renderMenuElement(item.subMenus, depth + 1)
              )}
            </Collapse>
          </React.Fragment>
        )
      })
    },
    [
      drawerNavigationMenuState,
      classes,
      activeRoute,
      handleClick,
      unreadCountNumber
    ]
  )

  //Handle logout
  const handleLogout = (e) => {
    e.preventDefault()
    dispatchLogoutStart()
  }

  const drawer = React.useMemo(
    () => (
      <div>
        <div className={`${classes.toolbar} ${classes.logoContainer}`}>
          <Typography variant="h3" gutterBottom className={classes.logo}>
            welcome to <span className={classes.version}> alfred,</span>
          </Typography>
          <Typography className={classes.firstName}>
            {firstName + '!'}
          </Typography>
          {/* <ThemeSwitcher /> */}
        </div>
        <List
          component="nav"
          aria-labelledby="nested-list-subheader"
          className={classes.drawerContainer}
        >
          {renderMenuElement(visibleMenus)}
          <MenuItem button onClick={handleLogout} className={classes.list}>
            <ListItemIcon>{menuIcons['Logout']}</ListItemIcon>
            <ListItemText primary="logout" />
          </MenuItem>
        </List>
      </div>
    ),
    [renderMenuElement, classes, visibleMenus]
  )

  const currentMenuItem = findMenuItemByPath(navigationMenus, path)
  const pageTitle = currentMenuItem ? currentMenuItem.displayName : 'alfred'

  return (
    <div className={classes.root}>
      <CssBaseline />
      {isMobile && (
        <MobileTopBar onMenuClick={handleDrawerToggle} title={pageTitle} />
      )}
      <nav className={classes.drawer}>
        {/* One drawer at a time, picked by the same useIsMobile() hook that
            governs MobileBottomNav / MobileTopBar. The previous Hidden+Drawer
            split was unreliable on mobile because Drawer variant="temporary"
            portals to document.body, so Hidden's display:none on the wrapper
            didn't always hide the desktop-style sidebar — you could end up
            with the permanent left drawer rendering at <600px while the
            bottom sheet was also wired up. Conditional render avoids the
            double-mount entirely. */}
        {isMobile ? (
          <Drawer
            variant="temporary"
            anchor="bottom"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            classes={{
              paper: classes.drawerPaperMobile
            }}
            ModalProps={{
              keepMounted: true // Better open performance on mobile.
            }}
          >
            <div className={classes.drawerHandle} aria-hidden="true" />
            {drawer}
          </Drawer>
        ) : (
          <Drawer
            classes={{
              paper: classes.drawerPaper
            }}
            variant="permanent"
            open
          >
            {drawer}
          </Drawer>
        )}
      </nav>
      <main className={isMobile ? classes.contentMobile : classes.content}>
        <div className={classes.toolbar} />
        {isMobile && <MobileBetterOnDesktop />}
        {props.children}
      </main>
      {isMobile && <MobileBottomNav />}
      <BruceWidget onConfig={onBruceConfig} />
    </div>
  )
}

MainLayout.propTypes = {
  children: PropTypes.node
}

export default MainLayout
